import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { EyeIcon, GaugeIcon, MessageSquareIcon, ShieldCheckIcon } from 'lucide-react';
import { PageCTA } from '../components/PageCTA';
const capabilities = [{
  icon: EyeIcon,
  title: 'Claridad de propuesta',
  description: 'Ayudamos a que el visitante pueda entender rápidamente qué haces, para quién y por qué elegirte.'
}, {
  icon: MessageSquareIcon,
  title: 'Caminos de contacto',
  description: 'Organizamos llamadas a la acción y puntos de contacto para facilitar una siguiente conversación.'
}, {
  icon: GaugeIcon,
  title: 'Experiencia ágil',
  description: 'Diseñamos con atención a lectura, jerarquía y comportamiento responsive para reducir fricción.'
}, {
  icon: ShieldCheckIcon,
  title: 'Confianza visible',
  description: 'Una presencia coherente hace más fácil que la primera impresión esté a la altura de tu negocio.'
}];
const scenarios = [{
  sector: 'Hostelería',
  challenge: 'Una carta difícil de consultar desde móvil y una web que no refleja la propuesta del local.',
  response: 'Una presencia editorial y una carta digital ordenada alrededor de categorías, horarios y acciones principales.',
  signal: 'Qué observar: facilidad para encontrar, elegir y contactar.'
}, {
  sector: 'Servicios locales',
  challenge: 'Información repartida, servicios poco definidos y una primera impresión que no explica la diferencia.',
  response: 'Una estructura que presenta servicios, proceso y puntos de contacto con una jerarquía más clara.',
  signal: 'Qué observar: calidad de las consultas y claridad de la propuesta.'
}, {
  sector: 'Marca profesional',
  challenge: 'Una presencia visual genérica que no acompaña el nivel de experiencia que se quiere comunicar.',
  response: 'Una dirección visual sobria y contenidos organizados para reforzar criterio, foco y confianza.',
  signal: 'Qué observar: percepción de marca y comprensión del servicio.'
}];
export function ResultsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen bg-[#0a0a0a] pt-20 sm:pt-24" data-id="element-489" data-mp="1ggad6shhdjfj">
      <section className="page-header" data-id="element-490" data-mp="1gdxo7vdb28sz">
        <div className="container mx-auto max-w-6xl px-6 md:px-12" data-id="element-491" data-mp="1l78zq9t2bdr8">
          <motion.div initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.26,
          ease: [0.23, 1, 0.32, 1]
        }} className="grid gap-7 lg:grid-cols-[0.8fr_1.2fr] lg:items-end" data-id="element-492" data-mp="1l8k3waqf6niv">
            <div data-id="element-493" data-mp="243iivbitmui3">
              <p className="eyebrow" data-id="element-494" data-mp="4sh6xvs084he">Enfoque</p>
              <h1 className="mt-5 text-4xl font-medium tracking-[-0.055em] text-white sm:text-6xl" data-id="element-495" data-mp="4tsiihroygj2">
                Diseñamos condiciones para que una presencia funcione mejor.
              </h1>
            </div>
            <p className="max-w-2xl text-base leading-relaxed text-neutral-400 sm:text-lg" data-id="element-496" data-mp="29m7jq4ntpt0">
              No usamos cifras genéricas como prueba. Ponemos el foco en los
              elementos que se pueden observar y mejorar: claridad, confianza,
              experiencia y facilidad para actuar.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section-shell border-b border-white/10" data-id="element-497" data-mp="l76vmuzlokck">
        <div className="container mx-auto max-w-6xl px-6 md:px-12" data-id="element-498" data-mp="g7u8080wn98x">
          <div className="grid overflow-hidden border border-white/10 sm:grid-cols-2 lg:grid-cols-4" data-id="element-499" data-mp="g8lrikh89giu">
            {capabilities.map((item, index) => {
            const Icon = item.icon;
            return <motion.article key={item.title} initial={{
              opacity: 0,
              y: 14
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              amount: 0.1
            }} transition={{
              duration: 0.26,
              delay: index * 0.04,
              ease: [0.23, 1, 0.32, 1]
            }} className="min-h-[235px] border-b border-white/10 bg-black p-7 last:border-b-0 sm:border-r sm:even:border-r-0 lg:border-b-0 lg:even:border-r lg:last:border-r-0" data-id="element-500" data-mp="6c0mw094e7fj" data-mpi={index}>
                  <Icon size={20} className="text-white" aria-hidden="true" data-id="element-501" data-mp="17dhqnumj4fpp" />
                  <h2 className="mt-10 text-lg font-medium text-white" data-id="element-502" data-mp="sg751sg69tg9">
                    {item.title}
                  </h2>
                  <p className="mt-3 text-sm leading-relaxed text-neutral-300" data-id="element-503" data-mp="xe8qul4zg6vf">
                    {item.description}
                  </p>
                </motion.article>;
          })}
          </div>
        </div>
      </section>

      <section className="section-shell border-b border-white/10 bg-black" aria-labelledby="scenarios-title" data-id="element-504" data-mp="20861gitk1gym">
        <div className="container mx-auto max-w-6xl px-6 md:px-12" data-id="element-505" data-mp="1v92wszhhlcr6">
          <div className="grid gap-7 border-b border-white/10 pb-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-end" data-id="element-506" data-mp="299ngdxv87clq">
            <div data-id="element-507" data-mp="258trp29yltrs">
              <p className="eyebrow" data-id="element-508" data-mp="258aa5mo817ea">Escenarios de aplicación</p>
              <h2 id="scenarios-title" className="mt-5 text-3xl font-medium tracking-[-0.045em] text-white sm:text-4xl" data-id="element-509" data-mp="1hhoaaiodetfd">
                Cómo se traduce el enfoque según el contexto.
              </h2>
            </div>
            <p className="max-w-2xl text-sm leading-relaxed text-neutral-300 sm:text-base" data-id="element-510" data-mp="1qccjul0zax9z">
              Estos escenarios son ilustrativos. No representan casos de éxito
              ni resultados atribuidos a clientes específicos.
            </p>
          </div>
          <div className="mt-10 space-y-4" data-id="element-511" data-mp="ts3to6fow899">
            {scenarios.map((scenario, index) => <motion.article key={scenario.sector} initial={{
            opacity: 0,
            y: 14
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.1
          }} transition={{
            duration: 0.26,
            delay: index * 0.04,
            ease: [0.23, 1, 0.32, 1]
          }} className="border border-white/10 bg-[#0a0a0a] p-7 sm:p-8" data-id="element-512" data-mp="2swln2bts8jqu" data-mpi={index}>
                <h3 className="text-xl font-medium tracking-[-0.025em] text-white" data-id="element-513" data-mp="2jz1k8yg9ufe4">
                  {scenario.sector}
                </h3>
                <div className="mt-7 grid gap-6 md:grid-cols-3" data-id="element-514" data-mp="3vasov6eruqw3">
                  <div data-id="element-515" data-mp="45j907khrt07">
                    <p className="eyebrow" data-id="element-516" data-mp="44rk1flegtiw">Situación</p>
                    <p className="mt-3 text-sm leading-relaxed text-neutral-300" data-id="element-517" data-mp="47dsrik11f2p">
                      {scenario.challenge}
                    </p>
                  </div>
                  <div data-id="element-518" data-mp="1jp0k4n9gyk94">
                    <p className="eyebrow" data-id="element-519" data-mp="1jquvpayup00j">Dirección</p>
                    <p className="mt-3 text-sm leading-relaxed text-neutral-300" data-id="element-520" data-mp="1ewqpitxesw0i">
                      {scenario.response}
                    </p>
                  </div>
                  <div data-id="element-521" data-mp="14zcgvghq9bir">
                    <p className="eyebrow" data-id="element-522" data-mp="150nl1hf34lae">Indicador</p>
                    <p className="mt-3 text-sm leading-relaxed text-white" data-id="element-523" data-mp="10y0pp6j7gi8g">
                      {scenario.signal}
                    </p>
                  </div>
                </div>
              </motion.article>)}
          </div>
        </div>
      </section>
      <PageCTA data-id="element-524" data-mp="r1nwsavhi8er" />
    </div>;
}